import { selectAggregateReportingWindow } from "../platformAnalytics/toplineReportingWindow";
import { formatFlightRange } from "./flightDates";
import {
  CANONICAL_CTR_DEFINITION,
  CANONICAL_CTR_LABEL,
} from "./canonicalCtr";
import { aggregateMetricBasis } from "../platformAnalytics/metricBasis";
import { normalizeAdvertiserVertical } from "../config/verticalDashboardConfig";

const PLATFORM_LABELS = {
  facebook: "Facebook",
  instagram: "Instagram",
  tiktok: "TikTok",
  youtube: "YouTube",
  x: "X",
  truth_social: "Truth Social",
  truthsocial: "Truth Social",
  rumble: "Rumble",
  google_search: "Google Search",
  reddit: "Reddit",
  amazon: "Amazon",
  shopify: "Shopify",
  linkedin: "LinkedIn Ads",
  community: "Community",
  metricool: "Community",
  audio: "Audio",
  ingame: "In-Game",
  in_game: "In-Game",
  ctv: "CTV",
  video: "Video",
  mobile: "Mobile",
  display: "Display",
  dooh: "DOOH",
  email: "Email",
};

export const SOCIAL_INTELLIGENCE_RANKING_CONTEXT = "social_intelligence";

const SOCIAL_TOP_PLATFORM_RANKING_MODEL =
  "delivery_engagement_efficiency_video_composite_v1";

const SOCIAL_TOP_PLATFORM_REASON_LABELS = {
  conversions: "strong conversion volume",
  clicks: "high click volume",
  ctr: "strong click-through rate",
  cpa: "efficient cost per acquisition",
  cpc: "efficient cost per click",
  cpm: "efficient CPM",
  engagements: "strong engagement volume",
  impressions: "broad delivery scale",
  videoViews: "meaningful video view volume",
  vvr: "strong video view rate",
  vcr: "strong video completion rate",
};

function isSocialRankingDebugEnabled() {
  try {
    if (
      typeof window !== "undefined" &&
      (window.__CPHERE_ANALYTICS_DEBUG === true ||
        Array.isArray(window.__CPHERE_ANALYTICS_DEBUG))
    ) {
      return true;
    }
  } catch {}
  return false;
}

export function shouldExposeSocialRankingDiagnostics() {
  return process.env.NODE_ENV !== "production" || isSocialRankingDebugEnabled();
}

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function readMetricNumber(source = {}, keys = []) {
  if (!source || typeof source !== "object") return null;
  for (const key of keys) {
    const value = toNumber(source?.[key]);
    if (value != null) return value;
  }
  return null;
}

function resolveToplineSpend(raw = {}) {
  return (
    toNumber(raw.clientFacingSpend) ??
    toNumber(raw.client_facing_spend) ??
    toNumber(raw.clientSpend) ??
    toNumber(raw.client_spend) ??
    toNumber(raw.spendClientFacing) ??
    toNumber(raw.spend_client_facing) ??
    toNumber(raw.clientBilledSpend) ??
    toNumber(raw.client_billed_spend) ??
    toNumber(raw.billedSpend) ??
    toNumber(raw.billed_spend) ??
    toNumber(raw.effectiveSpend) ??
    toNumber(raw.effective_spend) ??
    toNumber(raw.effectiveSpendRaw) ??
    toNumber(raw.spend) ??
    toNumber(raw.rawSpend) ??
    toNumber(raw.raw_spend) ??
    toNumber(raw.platformSpend) ??
    toNumber(raw.platform_spend) ??
    toNumber(raw.mediaSpend) ??
    toNumber(raw.media_spend) ??
    toNumber(raw.providerSpend) ??
    toNumber(raw.provider_spend)
  );
}

function formatCurrency(value, digits = 0) {
  const num = toNumber(value);
  if (num == null) return null;
  return `$${num.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;
}

function formatInteger(value) {
  const num = toNumber(value);
  if (num == null) return null;
  return Math.round(num).toLocaleString("en-US");
}

function formatPercent(value, digits = 2) {
  const num = toNumber(value);
  if (num == null) return null;
  return `${num.toFixed(digits)}%`;
}

function roundDiagnosticValue(value, digits = 4) {
  const num = toNumber(value);
  if (num == null) return null;
  return Number(num.toFixed(digits));
}

function normalizePlatformKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

function normalizePlatformLabel(value) {
  const key = normalizePlatformKey(value);
  return PLATFORM_LABELS[key] || String(value || "").trim() || "Platform";
}

function normalizePercentValue(value) {
  const num = toNumber(value);
  if (num == null) return null;
  if (num > 0 && num <= 1) return num * 100;
  return num;
}

function derivePercent(numerator, denominator) {
  const safeNumerator = toNumber(numerator);
  const safeDenominator = toNumber(denominator);
  if (safeNumerator == null || safeDenominator == null || safeDenominator <= 0) {
    return null;
  }
  return (safeNumerator / safeDenominator) * 100;
}

function normalizePositiveScore(value, maxValue) {
  const safeValue = toNumber(value);
  const safeMaxValue = toNumber(maxValue);
  if (safeValue == null || safeValue <= 0 || safeMaxValue == null || safeMaxValue <= 0) {
    return null;
  }
  return Math.min(safeValue / safeMaxValue, 1);
}

function normalizeInverseScore(value, minValue) {
  const safeValue = toNumber(value);
  const safeMinValue = toNumber(minValue);
  if (safeValue == null || safeValue <= 0 || safeMinValue == null || safeMinValue <= 0) {
    return null;
  }
  return Math.min(safeMinValue / safeValue, 1);
}

function resolvePerformanceSignals(raw = {}) {
  const spend = resolveToplineSpend(raw) ?? 0;
  const impressions = readMetricNumber(raw, ["impressions"]) ?? 0;
  const clicks = readMetricNumber(raw, ["clicks"]) ?? 0;
  const conversions = readMetricNumber(raw, ["conversions"]) ?? 0;
  const engagements = readMetricNumber(raw, ["engagements"]) ?? 0;
  const videoViews =
    readMetricNumber(raw, [
      "trueViewViews",
      "true_view_views",
      "videoViews",
      "video_views",
      "views",
    ]) ?? 0;
  const videoStarts =
    readMetricNumber(raw, [
      "videoStarts",
      "video_started",
      "video_starts",
      "videoStarted",
      "pageStarts",
      "page_starts",
    ]) ?? 0;
  const videoCompletions =
    readMetricNumber(raw, [
      "videoCompletions",
      "video_completions",
      "video_completed",
      "videoCompleted",
      "completed_views",
      "views100",
      "videoViews100",
      "quartile_100",
      "q100",
    ]) ?? 0;
  const engagementRate =
    derivePercent(engagements, impressions) ??
    normalizePercentValue(
      /* `engagement_rate` is deliberately absent: on any payload carrying
         site analytics it is GA4's engaged-sessions rate, not this entry's
         paid engagement rate. The division above already answers from the
         entry's own counts. */
      readMetricNumber(raw, ["engagementRate", "engRate", "eng_rate"])
    );
  const ctr =
    derivePercent(clicks, impressions) ??
    normalizePercentValue(readMetricNumber(raw, ["ctr", "click_through_rate"]));
  const cpc =
    readMetricNumber(raw, ["cpc", "cost_per_click"]) ??
    (clicks > 0 && spend > 0 ? spend / clicks : null);
  const cpm =
    readMetricNumber(raw, ["cpm", "cost_per_mille"]) ??
    (impressions > 0 && spend > 0 ? (spend / impressions) * 1000 : null);
  const cpa =
    readMetricNumber(raw, ["cpa", "cost_per_acquisition", "cost_per_conversion"]) ??
    (conversions > 0 && spend > 0 ? spend / conversions : null);
  const vvr =
    derivePercent(videoViews, impressions) ??
    normalizePercentValue(
      readMetricNumber(raw, ["vvr", "videoViewRate", "video_view_rate", "viewRate", "view_rate"])
    );
  const vcr =
    derivePercent(videoCompletions, videoStarts) ??
    normalizePercentValue(
      readMetricNumber(raw, [
        "vcr",
        "videoCompletionRate",
        "video_completion_rate",
        "completionRate",
        "completion_rate",
      ])
    );

  return {
    spend,
    impressions,
    clicks,
    conversions,
    engagements,
    engagementRate,
    videoViews,
    videoStarts,
    videoCompletions,
    ctr,
    cpc,
    cpm,
    cpa,
    vvr,
    vcr,
  };
}

function getSocialPerformanceWeights(advertiserVertical = "general") {
  const normalizedVertical = normalizeAdvertiserVertical(advertiserVertical);

  if (normalizedVertical === "film") {
    return {
      conversions: 0.14,
      clicks: 0.1,
      ctr: 0.12,
      cpa: 0.1,
      cpc: 0.08,
      cpm: 0.05,
      engagements: 0.05,
      impressions: 0.04,
      spend: 0.03,
      videoViews: 0.08,
      vvr: 0.11,
      vcr: 0.1,
    };
  }

  return {
    conversions: 0.18,
    clicks: 0.14,
    ctr: 0.18,
    cpa: 0.14,
    cpc: 0.1,
    cpm: 0.06,
    engagements: 0.05,
    impressions: 0.04,
    spend: 0.03,
    videoViews: 0.03,
    vvr: 0.03,
    vcr: 0.02,
  };
}

function getLowVideoPenalty(signals, advertiserVertical = "general") {
  const normalizedVertical = normalizeAdvertiserVertical(advertiserVertical);
  let penalty = 0;

  if (signals.vvr != null) {
    if (signals.vvr < 1) {
      penalty += normalizedVertical === "film" ? 0.16 : 0.08;
    } else if (normalizedVertical === "film" && signals.vvr < 3) {
      penalty += 0.08;
    }
  }

  if (signals.vcr != null) {
    if (signals.vcr < 20) {
      penalty += normalizedVertical === "film" ? 0.12 : 0.05;
    } else if (normalizedVertical === "film" && signals.vcr < 40) {
      penalty += 0.06;
    }
  }

  return penalty;
}

function collectPositiveMaximum(entries, signalKey) {
  return entries.reduce((maxValue, entry) => {
    const value = toNumber(entry?.signals?.[signalKey]);
    if (value == null || value <= 0) return maxValue;
    return Math.max(maxValue, value);
  }, 0);
}

function collectPositiveMinimum(entries, signalKey) {
  const minimum = entries.reduce((minValue, entry) => {
    const value = toNumber(entry?.signals?.[signalKey]);
    if (value == null || value <= 0) return minValue;
    return Math.min(minValue, value);
  }, Infinity);
  return Number.isFinite(minimum) ? minimum : null;
}

function buildPerformanceReasons(metricScores = {}, weights = {}) {
  return Object.entries(SOCIAL_TOP_PLATFORM_REASON_LABELS)
    .map(([key, label]) => {
      const score = toNumber(metricScores[key]);
      const weight = toNumber(weights[key]);
      if (score == null || weight == null || score < 0.55) return null;
      return {
        label,
        contribution: score * weight,
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.contribution - a.contribution)
    .map((reason) => reason.label)
    .filter((label, index, labels) => labels.indexOf(label) === index)
    .slice(0, 3);
}

function buildPerformanceReasonSummary(entry) {
  const reasons = Array.isArray(entry?.performanceReasons) ? entry.performanceReasons : [];
  const penalty = roundDiagnosticValue(entry?.performancePenalty, 4);

  if (!reasons.length && !penalty) {
    return "No dominant performance signal was available.";
  }

  const summaryParts = [];
  if (reasons.length) {
    summaryParts.push(reasons.join(", "));
  }
  if (penalty) {
    summaryParts.push("video-quality penalty applied");
  }
  return summaryParts.join("; ");
}

function buildSocialRankingDiagnosticsEntry(entry) {
  return {
    platformKey: entry?.key || "",
    platformLabel: entry?.label || "",
    spend: roundDiagnosticValue(entry?.signals?.spend, 2),
    impressions: roundDiagnosticValue(entry?.signals?.impressions, 0),
    clicks: roundDiagnosticValue(entry?.signals?.clicks, 0),
    ctr: roundDiagnosticValue(entry?.signals?.ctr, 4),
    cpc: roundDiagnosticValue(entry?.signals?.cpc, 4),
    cpm: roundDiagnosticValue(entry?.signals?.cpm, 4),
    engagements: roundDiagnosticValue(entry?.signals?.engagements, 0),
    engagementRate: roundDiagnosticValue(entry?.signals?.engagementRate, 4),
    videoViews: roundDiagnosticValue(entry?.signals?.videoViews, 0),
    videoViewRate: roundDiagnosticValue(entry?.signals?.vvr, 4),
    videoCompletions: roundDiagnosticValue(entry?.signals?.videoCompletions, 0),
    videoCompletionRate: roundDiagnosticValue(entry?.signals?.vcr, 4),
    compositeScore: roundDiagnosticValue(entry?.performanceScore, 4),
    rankingPosition: entry?.rankingPosition || null,
    reasonSummary: buildPerformanceReasonSummary(entry),
  };
}

function buildSocialRankingDiagnostics(rankedEntries = [], advertiserVertical = "general") {
  return {
    model: SOCIAL_TOP_PLATFORM_RANKING_MODEL,
    advertiserVertical: normalizeAdvertiserVertical(advertiserVertical),
    winner: rankedEntries[0]?.label || "",
    winnerKey: rankedEntries[0]?.key || "",
    platforms: rankedEntries.map((entry) => buildSocialRankingDiagnosticsEntry(entry)),
  };
}

function chooseSpendLedTopPlatformEntry(included = []) {
  return included.reduce((best, current) => {
    const bestSpend = resolveToplineSpend(best?.topline?.raw || {}) || 0;
    const currentSpend = resolveToplineSpend(current?.topline?.raw || {}) || 0;
    if (currentSpend > bestSpend) return current;
    if (currentSpend === bestSpend) {
      const bestImpressions = toNumber(best?.topline?.raw?.impressions) || 0;
      const currentImpressions = toNumber(current?.topline?.raw?.impressions) || 0;
      if (currentImpressions > bestImpressions) return current;
    }
    return best;
  }, included[0] || null);
}

export function rankToplinePerformanceEntries(entries = [], { advertiserVertical = "general" } = {}) {
  const normalizedVertical = normalizeAdvertiserVertical(advertiserVertical);
  const preparedEntries = (Array.isArray(entries) ? entries : [])
    .map((entry) => {
      const raw = entry?.topline?.raw || entry?.raw || entry || {};
      return {
        ...entry,
        key: entry?.key || normalizePlatformKey(entry?.platformKey),
        label:
          entry?.label || normalizePlatformLabel(entry?.platformKey || entry?.key || entry?.label),
        raw,
        signals: resolvePerformanceSignals(raw),
      };
    })
    .filter((entry) => entry.key);

  if (!preparedEntries.length) return [];

  const maximums = {
    spend: collectPositiveMaximum(preparedEntries, "spend"),
    impressions: collectPositiveMaximum(preparedEntries, "impressions"),
    clicks: collectPositiveMaximum(preparedEntries, "clicks"),
    conversions: collectPositiveMaximum(preparedEntries, "conversions"),
    engagements: collectPositiveMaximum(preparedEntries, "engagements"),
    videoViews: collectPositiveMaximum(preparedEntries, "videoViews"),
    ctr: collectPositiveMaximum(preparedEntries, "ctr"),
    vvr: collectPositiveMaximum(preparedEntries, "vvr"),
    vcr: collectPositiveMaximum(preparedEntries, "vcr"),
  };
  const minimums = {
    cpa: collectPositiveMinimum(preparedEntries, "cpa"),
    cpc: collectPositiveMinimum(preparedEntries, "cpc"),
    cpm: collectPositiveMinimum(preparedEntries, "cpm"),
  };
  const weights = getSocialPerformanceWeights(normalizedVertical);

  const rankedEntries = preparedEntries
    .map((entry) => {
      const metricScores = {
        spend: normalizePositiveScore(entry.signals.spend, maximums.spend),
        impressions: normalizePositiveScore(entry.signals.impressions, maximums.impressions),
        clicks: normalizePositiveScore(entry.signals.clicks, maximums.clicks),
        conversions: normalizePositiveScore(entry.signals.conversions, maximums.conversions),
        engagements: normalizePositiveScore(entry.signals.engagements, maximums.engagements),
        videoViews: normalizePositiveScore(entry.signals.videoViews, maximums.videoViews),
        ctr: normalizePositiveScore(entry.signals.ctr, maximums.ctr),
        vvr: normalizePositiveScore(entry.signals.vvr, maximums.vvr),
        vcr: normalizePositiveScore(entry.signals.vcr, maximums.vcr),
        cpa: normalizeInverseScore(entry.signals.cpa, minimums.cpa),
        cpc: normalizeInverseScore(entry.signals.cpc, minimums.cpc),
        cpm: normalizeInverseScore(entry.signals.cpm, minimums.cpm),
      };

      let weightedScore = 0;
      let availableWeight = 0;

      Object.entries(weights).forEach(([metricKey, weight]) => {
        const score = toNumber(metricScores[metricKey]);
        if (score == null) return;
        weightedScore += score * weight;
        availableWeight += weight;
      });

      const baseScore = availableWeight > 0 ? weightedScore / availableWeight : 0;
      const videoPenalty = getLowVideoPenalty(entry.signals, normalizedVertical);

      return {
        ...entry,
        metricScores,
        performanceBaseScore: baseScore,
        performancePenalty: videoPenalty,
        performanceScore: Math.max(0, baseScore - videoPenalty),
        performanceReasons: buildPerformanceReasons(metricScores, weights),
        performanceRankingModel: SOCIAL_TOP_PLATFORM_RANKING_MODEL,
        advertiserVertical: normalizedVertical,
      };
    })
    .sort((a, b) => {
      if (b.performanceScore !== a.performanceScore) {
        return b.performanceScore - a.performanceScore;
      }
      if ((b.signals.conversions || 0) !== (a.signals.conversions || 0)) {
        return (b.signals.conversions || 0) - (a.signals.conversions || 0);
      }
      if ((b.signals.clicks || 0) !== (a.signals.clicks || 0)) {
        return (b.signals.clicks || 0) - (a.signals.clicks || 0);
      }
      if ((b.signals.impressions || 0) !== (a.signals.impressions || 0)) {
        return (b.signals.impressions || 0) - (a.signals.impressions || 0);
      }
      return (b.signals.spend || 0) - (a.signals.spend || 0);
    });

  return rankedEntries.map((entry, index) => {
    const rankingPosition = index + 1;
    const rankedEntry = {
      ...entry,
      rankingPosition,
    };

    if (!shouldExposeSocialRankingDiagnostics()) {
      return rankedEntry;
    }

    return {
      ...rankedEntry,
      diagnostics: buildSocialRankingDiagnosticsEntry(rankedEntry),
    };
  });
}

export function readStoredAnalyticsTimeframe() {
  try {
    const start = String(localStorage.getItem("selectedDateStart") || "").slice(0, 10) || null;
    const end = String(localStorage.getItem("selectedDateEnd") || "").slice(0, 10) || null;
    if (start && end) {
      return {
        start,
        end,
        datePreset: "",
        label: formatFlightRange(start, end),
        source: "explicit_user",
        isExplicitOverride: true,
      };
    }
  } catch {}

  return {
    start: null,
    end: null,
    datePreset: "last_30d",
    label: "Flight not available",
    source: "default_preset",
    isExplicitOverride: false,
  };
}

export const readStoredEdenTimeframe = readStoredAnalyticsTimeframe;

// Availability has already been established by the scoped reporting reader.
// Keep its measured zeros through the aggregate's final filters, but never
// coerce a missing sibling field (or a boolean/string) into a measurement.
const REPORTED_ZERO_FIELDS = {
  spend: ['clientFacingSpend', 'billedSpend', 'spend'],
  impressions: ['impressions'], clicks: ['clicks'], conversions: ['conversions'],
  engagements: ['engagements'], videoViews: ['trueViewViews', 'true_view_views', 'videoViews'],
  qualifiedVideoViews: ['qualifiedVideoViews'],
};
function hasReportedZeroMetric(topline, metric) {
  const raw = topline?.raw;
  return raw?.hasAnyData === true && raw?.usableDataFound === true &&
    REPORTED_ZERO_FIELDS[metric].some(key => typeof raw[key] === 'number' && raw[key] === 0);
}

export function hasMeaningfulToplineData(topline) {
  const raw = topline?.raw || {};
  const candidates = [
    resolveToplineSpend(raw),
    raw.impressions,
    raw.clicks,
    raw.conversions,
    raw.engagements,
    raw.trueViewViews ?? raw.true_view_views ?? raw.videoViews,
    raw.sessions,
  ];

  return Object.keys(REPORTED_ZERO_FIELDS).some(metric => hasReportedZeroMetric(topline, metric)) ||
    candidates.some((value) => {
      const num = toNumber(value);
      return num != null && num > 0;
    });
}

export function buildAggregateToplineSnapshot({
  pageLabel,
  platforms,
  toplines,
  advertiserVertical = "general",
  rankingContext = "default",
}) {
  const uniquePlatforms = [];
  const seen = new Set();

  (Array.isArray(platforms) ? platforms : []).forEach((platform) => {
    const key = normalizePlatformKey(platform?.key);
    if (!key || seen.has(key)) return;
    seen.add(key);
    uniquePlatforms.push({
      key,
      label: normalizePlatformLabel(platform?.label || key),
    });
  });

  const candidates = uniquePlatforms
    .map((platform) => ({
      ...platform,
      topline: toplines?.[platform.key] || null,
    }))
    .filter((entry) => hasMeaningfulToplineData(entry.topline));

  const reporting = selectAggregateReportingWindow(candidates);
  const included = reporting.included;
  const unavailablePlatforms = uniquePlatforms.filter(platform => !candidates.some(entry => entry.key === platform.key));
  reporting.metadata.unavailablePlatforms = unavailablePlatforms;
  if (included.length && unavailablePlatforms.length) reporting.metadata.reportingWindowStatus = "partial";

  if (!included.length) {
    return {
      metrics: [],
      metadata: {
        ...reporting.metadata,
        includedPlatformKeys: [],
        includedPlatformLabels: [],
        ctrIncludedPlatformKeys: [],
        ctrIncludedPlatformLabels: [],
        ctrExcludedPlatformKeys: [],
        eligiblePlatformCount: 0,
        topPlatform: "",
        topPlatformKey: "",
      },
    };
  }

  const totals = included.reduce(
    (acc, entry) => {
      const raw = entry.topline?.raw || {};
      acc.spend += resolveToplineSpend(raw) || 0;
      acc.impressions += toNumber(raw.impressions) || 0;
      acc.clicks += toNumber(raw.clicks) || 0;
      acc.conversions += toNumber(raw.conversions) || 0;
      acc.engagements += toNumber(raw.engagements) || 0;
      acc.videoViews +=
        toNumber(raw.trueViewViews ?? raw.true_view_views ?? raw.videoViews) || 0;
      /*
       * The governed qualified-video total, carried alongside the generic
       * count so this tile can lead with the measure a reader actually means
       * by "video". Each entry's figure is already resolved by the canonical
       * engine; summing them here aggregates across advertisers, exactly as
       * every other total on this tile does.
       */
      acc.qualifiedVideoViews += toNumber(raw.qualifiedVideoViews) || 0;
      acc.sessions += toNumber(raw.sessions) || 0;
      return acc;
    },
    {
      spend: 0,
      impressions: 0,
      clicks: 0,
      conversions: 0,
      engagements: 0,
      videoViews: 0,
      qualifiedVideoViews: 0,
      sessions: 0,
    }
  );

  /*
   * Blended rates over the included platforms, through the canonical
   * eligibility engine: each rate divides only the observations that can
   * participate in it, and CPC/CPA spend is the eligible platforms' own
   * spend, never the pool's. `ctrExcludedPlatformKeys` is finally an honest
   * field — it used to be hardcoded [] while promising exclusions.
   */
  const rateObservations = included.map((entry) => ({
    platformKey: entry.key,
    sourceKey: entry?.topline?.raw?.snapshotDataSource || entry.key,
    metrics: entry?.topline?.raw || {},
  }));
  const ctrBasis = aggregateMetricBasis("ctr", rateObservations);
  const ctrTotals = {
    clicks: ctrBasis.numerator ?? 0,
    impressions: ctrBasis.denominator ?? 0,
    platformKeys: ctrBasis.eligiblePlatformKeys,
    platformLabels: included
      .filter((entry) => ctrBasis.eligiblePlatformKeys.includes(entry.key))
      .map((entry) => entry.label),
  };
  const ctr = ctrBasis.value;
  const ctrExcludedPlatformKeys = ctrBasis.excludedPlatformKeys;
  const cpm = aggregateMetricBasis("cpm", rateObservations).value;
  const cpc = aggregateMetricBasis("cpc", rateObservations).value;
  /* CPA is attribution-sensitive: it blends only when every participant
     shares one source definition. No proof exists at this layer, so a
     multi-source scope reports unavailable rather than a manufactured
     number. */
  const cpa = aggregateMetricBasis("cpa", rateObservations).value;

  const useSocialPerformanceRanking =
    rankingContext === SOCIAL_INTELLIGENCE_RANKING_CONTEXT;
  const rankedTopPlatformEntries = useSocialPerformanceRanking
    ? rankToplinePerformanceEntries(included, { advertiserVertical })
    : [];
  const topPlatformDiagnostics =
    useSocialPerformanceRanking && shouldExposeSocialRankingDiagnostics()
      ? buildSocialRankingDiagnostics(rankedTopPlatformEntries, advertiserVertical)
      : null;
  const topPlatformRanking = rankedTopPlatformEntries[0] || null;
  const topPlatformEntry = useSocialPerformanceRanking
    ? topPlatformRanking || null
    : chooseSpendLedTopPlatformEntry(included);
  const sourceTierCounts = included.reduce((acc, entry) => {
    const tier = String(entry?.topline?.raw?.snapshotSourceTier || "")
      .trim()
      .toLowerCase();
    if (!tier) return acc;
    acc[tier] = (acc[tier] || 0) + 1;
    return acc;
  }, {});
  const includedSourceTiers = Object.keys(sourceTierCounts);
  const hasLiveSource = Boolean(sourceTierCounts.live_direct);
  const hasReportUrlFallback = Boolean(sourceTierCounts.report_url_scan);
  const hasSnapshotFallback = Boolean(sourceTierCounts.stored_snapshot);
  const primarySourceTier =
    hasLiveSource
      ? "live_direct"
      : hasReportUrlFallback
      ? "report_url_scan"
      : hasSnapshotFallback
      ? "stored_snapshot"
      : "";
  const primarySourceLabel =
    String(topPlatformEntry?.topline?.raw?.snapshotSourceLabel || "").trim() ||
    (primarySourceTier === "live_direct"
      ? "Live Feed"
      : primarySourceTier === "report_url_scan"
      ? "Report URL Fallback"
      : primarySourceTier === "stored_snapshot"
      ? "Snapshot Fallback"
      : "");
  const sourceBreakdownByPlatform = Object.fromEntries(
    included.map((entry) => [
      entry.key,
      {
        tier: entry?.topline?.raw?.snapshotSourceTier || null,
        label: entry?.topline?.raw?.snapshotSourceLabel || null,
      },
    ])
  );
  const topPlatformSourceTier = String(
    topPlatformEntry?.topline?.raw?.snapshotSourceTier || ""
  )
    .trim()
    .toLowerCase();
  const topPlatformSourceLabel = String(
    topPlatformEntry?.topline?.raw?.snapshotSourceLabel || ""
  ).trim();

  const hasReportedZero = metric => included.some(entry => hasReportedZeroMetric(entry.topline, metric));
  const platformMeta = useSocialPerformanceRanking
    ? "Client-billed spend across live social platforms"
    : `${included.length} contributing platform${included.length === 1 ? "" : "s"} in ${pageLabel}`;
  const metrics = [
    (totals.spend > 0 || hasReportedZero('spend'))
      ? {
          id: "spend",
          label: "Spend",
          value: formatCurrency(totals.spend, totals.spend < 1 ? 2 : 0) || "—",
          meta: platformMeta,
        }
      : null,
    (totals.impressions > 0 || hasReportedZero('impressions'))
      ? {
          id: "impressions",
          label: "Impressions",
          value: formatInteger(totals.impressions) || "—",
          meta: useSocialPerformanceRanking
            ? "Live social reach"
            : "Aggregate reach across contributing platforms",
        }
      : null,
    (totals.clicks > 0 || hasReportedZero('clicks'))
      ? {
          id: "clicks",
          label: "Clicks",
          value: formatInteger(totals.clicks) || "—",
          meta: useSocialPerformanceRanking
            ? "Live social click volume"
            : "Aggregate click volume",
        }
      : null,
    ctr != null
      ? {
          id: "ctr",
          label: useSocialPerformanceRanking ? "Blended social CTR" : CANONICAL_CTR_LABEL,
          value: formatPercent(ctr) || "—",
          meta: useSocialPerformanceRanking
            ? "Clicks divided by impressions across live social platforms"
            : `Derived from total clicks and impressions across ${ctrTotals.platformKeys.length} contributing platform${ctrTotals.platformKeys.length === 1 ? "" : "s"}`,
        }
      : null,
    cpm != null
      ? {
          id: "cpm",
          label: "CPM",
          value: formatCurrency(cpm, 2) || "—",
          meta: "Derived from aggregate spend and impressions",
        }
      : null,
    cpa != null
      ? {
          id: "cpa",
          label: "CPA",
          value: formatCurrency(cpa, 2) || "—",
          meta: "Derived from aggregate spend and conversions",
        }
      : cpc != null
      ? {
          id: "cpc",
          label: "CPC",
          value: formatCurrency(cpc, 2) || "—",
          meta: "Derived from aggregate spend and clicks",
        }
      : null,
  ].filter(Boolean);

  if (metrics.length < 6 && (totals.conversions > 0 || hasReportedZero('conversions'))) {
    metrics.push({
      id: "conversions",
      label: "Conversions",
      value: formatInteger(totals.conversions) || "—",
      meta: "Aggregate observed conversions",
    });
  }

  if (metrics.length < 6 && (totals.engagements > 0 || hasReportedZero('engagements'))) {
    metrics.push({
      id: "engagements",
      label: "Engagements",
      value: formatInteger(totals.engagements) || "—",
      meta: "Aggregate engagement signal",
    });
  }

  /*
   * One vocabulary across surfaces.
   *
   * A reader seeing "Video Views" on a dashboard tile means total meaningful
   * video consumption, so this leads with the qualified total wherever the
   * canonical layer resolved one — otherwise this tile would show 28.1K while
   * Campaign Insights showed 167.3K under effectively the same word, which is
   * the confusion the qualified metric was created to end. The generic count
   * still stands in for scopes that have no qualified signal, under its own
   * narrower name.
   */
  if (metrics.length < 6 && (totals.qualifiedVideoViews > 0 || hasReportedZero('qualifiedVideoViews'))) {
    metrics.push({
      id: "qualified_video_views",
      label: "Qualified Video Views",
      value: formatInteger(totals.qualifiedVideoViews) || "—",
      meta: "Each video platform's qualified viewing signal",
    });
  } else if (metrics.length < 6 && (totals.videoViews > 0 || hasReportedZero('videoViews'))) {
    metrics.push({
      id: "video_views",
      label: "Video Views",
      value: formatInteger(totals.videoViews) || "—",
      meta: "Aggregate video-view signal",
    });
  }

  if (metrics.length < 6 && totals.sessions > 0) {
    metrics.push({
      id: "sessions",
      label: "Sessions",
      value: formatInteger(totals.sessions) || "—",
      meta: "GA support signal",
    });
  }

  return {
    metrics: metrics.slice(0, 6),
    metadata: {
      ...reporting.metadata,
      includedPlatformKeys: included.map((entry) => entry.key),
      includedPlatformLabels: included.map((entry) => entry.label),
      ctrIncludedPlatformKeys: ctrTotals.platformKeys,
      ctrIncludedPlatformLabels: ctrTotals.platformLabels,
      ctrExcludedPlatformKeys,
      ctrDefinition: CANONICAL_CTR_DEFINITION,
      eligiblePlatformCount: included.length,
      topPlatform: topPlatformEntry?.label || "",
      topPlatformKey: topPlatformEntry?.key || "",
      topPlatformRankingModel: useSocialPerformanceRanking
        ? topPlatformRanking?.performanceRankingModel || SOCIAL_TOP_PLATFORM_RANKING_MODEL
        : "spend_then_impressions_v1",
      topPlatformScore:
        useSocialPerformanceRanking && topPlatformRanking
          ? Number(topPlatformRanking.performanceScore.toFixed(4))
          : null,
      topPlatformReasons: useSocialPerformanceRanking
        ? topPlatformRanking?.performanceReasons || []
        : [],
      topPlatformAdvertiserVertical: useSocialPerformanceRanking
        ? normalizeAdvertiserVertical(advertiserVertical)
        : null,
      topPlatformSourceTier,
      topPlatformSourceLabel,
      includedSourceTiers,
      sourceTierCounts,
      primarySourceTier,
      primarySourceLabel,
      hasLiveSource,
      hasReportUrlFallback,
      hasSnapshotFallback,
      sourceBreakdownByPlatform,
      ...(topPlatformDiagnostics ? { topPlatformDiagnostics } : {}),
    },
  };
}
